import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Mail, Lock, User, Loader2, ArrowLeft, Shield, Eye, EyeOff } from 'lucide-react';

// Auto-detect API URL based on current domain
const getApiUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  }
  // Use same origin + /api for production
  return window.location.origin + '/api';
};

const API = getApiUrl();

export function AuthModal({ isOpen, onClose, onSuccess, initialTab = 'login' }) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    inviteCode: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: formData.email.trim().toLowerCase(),
          password: formData.password
        })
      });

      // Try to parse JSON; some errors (e.g. rate-limit HTML) may not be JSON
      let data = null;
      try { data = await response.json(); } catch { data = null; }

      if (response.ok) {
        toast.success('Welcome back!');
        onSuccess(data);
        onClose();
      } else if (response.status === 401) {
        // Backend returns "Invalid email or password" for both wrong-email and wrong-password.
        toast.error(data?.detail || 'Password incorrect. Please try again.');
      } else if (response.status === 429) {
        toast.error('Too many attempts. Please wait a minute and try again.');
      } else {
        toast.error(data?.detail || 'Password incorrect. Please try again.');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Connection error. Please check your internet and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailRegister = async (e) => {
    e.preventDefault();
    
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (!formData.inviteCode) {
      toast.error('Invite code is required to join');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          name: formData.name,
          invite_code: formData.inviteCode
        })
      });

      if (response.ok) {
        const userData = await response.json();
        toast.success('Account created! Welcome to The Bundle Golf Club!');
        onSuccess(userData);
        onClose();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Registration failed');
      }
    } catch (error) {
      toast.error('An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    if (!formData.email) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email })
      });

      if (response.ok) {
        setResetEmailSent(true);
        toast.success('Password reset instructions sent!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send reset email');
      }
    } catch (error) {
      toast.error('An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (formData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          new_password: formData.newPassword
        })
      });

      if (response.ok) {
        toast.success('Password reset successfully! Please log in.');
        setShowForgotPassword(false);
        setResetEmailSent(false);
        setActiveTab('login');
        setFormData({ ...formData, password: '', newPassword: '', confirmPassword: '' });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reset password');
      }
    } catch (error) {
      toast.error('An error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Forgot Password View
  if (showForgotPassword) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-bundle-green text-center">
              Reset Password
            </DialogTitle>
          </DialogHeader>

          <div className="mt-4">
            <button
              onClick={() => {
                setShowForgotPassword(false);
                setResetEmailSent(false);
              }}
              className="flex items-center gap-2 text-bundle-green hover:text-bundle-green-dark font-body text-sm mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Sign In
            </button>

            {!resetEmailSent ? (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <p className="font-body text-gray-600 text-sm">
                  Enter your email address and we'll help you reset your password.
                </p>
                <div className="space-y-2">
                  <Label htmlFor="reset-email" className="font-body text-bundle-green">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="reset-email"
                      type="email"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="pl-10 border-bundle-cream-dark focus:border-bundle-green"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-bundle py-5"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="bg-bundle-green/10 border border-bundle-green/20 rounded-lg p-4 mb-4">
                  <p className="font-body text-sm text-bundle-green">
                    ✓ Email verified: <strong>{formData.email}</strong>
                  </p>
                  <p className="font-body text-xs text-gray-600 mt-1">
                    Enter your new password below.
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="new-password" className="font-body text-bundle-green">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="new-password"
                      data-testid="reset-new-password-input"
                      type={showNewPassword ? 'text' : 'password'}
                      placeholder="Min 6 characters"
                      value={formData.newPassword}
                      onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                      required
                      minLength={6}
                      className="pl-10 pr-10 border-bundle-cream-dark focus:border-bundle-green"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(v => !v)}
                      aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-bundle-green transition-colors p-1"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirm-password" className="font-body text-bundle-green">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="confirm-password"
                      data-testid="reset-confirm-password-input"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm new password"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      required
                      className="pl-10 pr-10 border-bundle-cream-dark focus:border-bundle-green"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(v => !v)}
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-bundle-green transition-colors p-1"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-bundle py-5"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </Button>
              </form>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-bundle-green text-center">
            {activeTab === 'login' ? 'Welcome Back!' : 'Join The Club'}
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4">
          {/* Tabs */}
          <div className="flex mb-6 border border-bundle-cream-dark rounded-lg overflow-hidden">
            <button
              onClick={() => setActiveTab('login')}
              className={`flex-1 py-3 font-body text-sm transition-colors ${
                activeTab === 'login'
                  ? 'bg-bundle-green text-white'
                  : 'text-bundle-green hover:bg-bundle-cream/50'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => setActiveTab('register')}
              className={`flex-1 py-3 font-body text-sm transition-colors ${
                activeTab === 'register'
                  ? 'bg-bundle-green text-white'
                  : 'text-bundle-green hover:bg-bundle-cream/50'
              }`}
            >
              Create Account
            </button>
          </div>

          {/* Login Form */}
          {activeTab === 'login' && (
            <form onSubmit={handleEmailLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="login-email" className="font-body text-bundle-green">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="pl-10 border-bundle-cream-dark focus:border-bundle-green"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="login-password" className="font-body text-bundle-green">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="login-password"
                    data-testid="login-password-input"
                    type={showLoginPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    className="pl-10 pr-10 border-bundle-cream-dark focus:border-bundle-green"
                  />
                  <button
                    type="button"
                    onClick={() => setShowLoginPassword(v => !v)}
                    data-testid="toggle-login-password"
                    aria-label={showLoginPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-bundle-green transition-colors p-1"
                  >
                    {showLoginPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-sm text-bundle-gold hover:text-bundle-gold-dark font-body"
              >
                Forgot password?
              </button>
              
              <Button
                type="submit"
                disabled={loading}
                className="w-full btn-bundle py-5"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>
          )}

          {/* Register Form */}
          {activeTab === 'register' && (
            <form onSubmit={handleEmailRegister} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="register-name" className="font-body text-bundle-green">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="register-name"
                    type="text"
                    placeholder="John Smith"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="pl-10 border-bundle-cream-dark focus:border-bundle-green"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="register-email" className="font-body text-bundle-green">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="register-email"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="pl-10 border-bundle-cream-dark focus:border-bundle-green"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="register-password" className="font-body text-bundle-green">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="register-password"
                    data-testid="register-password-input"
                    type={showRegisterPassword ? 'text' : 'password'}
                    placeholder="Min 6 characters"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    minLength={6}
                    className="pl-10 pr-10 border-bundle-cream-dark focus:border-bundle-green"
                  />
                  <button
                    type="button"
                    onClick={() => setShowRegisterPassword(v => !v)}
                    data-testid="toggle-register-password"
                    aria-label={showRegisterPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-bundle-green transition-colors p-1"
                  >
                    {showRegisterPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="register-invite" className="font-body text-bundle-green">Invite Code</Label>
                <div className="relative">
                  <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="register-invite"
                    data-testid="register-invite-code"
                    type="text"
                    placeholder="Enter invite code"
                    value={formData.inviteCode}
                    onChange={(e) => setFormData({ ...formData, inviteCode: e.target.value.toUpperCase() })}
                    required
                    className="pl-10 border-bundle-cream-dark focus:border-bundle-green uppercase"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="register-submit-btn"
                className="w-full btn-bundle py-5"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  'Create Account & Start Free Trial'
                )}
              </Button>
              <p className="text-xs text-center text-gray-500 font-body">
                By signing up, you get 60 days free access
              </p>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
